/**
 * TranscriptPage — view transcript, summary, sentiment, and action items for a recording.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = '/api/ai';
const token = () => localStorage.getItem('token');
const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

const SENTIMENT_COLOR: Record<string, string> = {
  positive: '#10b981',
  negative: '#ef4444',
  neutral: '#64748b',
  mixed: '#f59e0b',
};

const PRIORITY_COLOR: Record<string, string> = {
  high: '#ef4444',
  medium: '#f59e0b',
  low: '#10b981',
};

type Segment = { start: number; end: number; text: string };
type ActionItem = { item: string; assignee: string; priority: string };

interface Transcript {
  id: number;
  recording_id: number;
  language: string;
  language_probability: number;
  transcript_text: string;
  word_count: number;
  duration_seconds: number;
  whisper_model: string;
  segments: Segment[];
  created_at: string;
  filename: string;
  original_name: string;
  username: string;
}

interface Analysis {
  executive_summary: string;
  detailed_summary: string;
  action_items: ActionItem[];
  sentiment: string;
  sentiment_score: number;
  sentiment_detail: Record<string, any>;
  keywords: string[];
  topics: string[];
  llm_model: string;
}

interface Props {
  recordingId: number;
  onBack: () => void;
}

export default function TranscriptPage({ recordingId, onBack }: Props) {
  const [tab, setTab] = useState<'transcript' | 'summary' | 'sentiment' | 'actions'>('transcript');
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [copied, setCopied] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [tRes, aRes] = await Promise.allSettled([
        axios.get(`${API}/transcripts/${recordingId}`, auth()),
        axios.get(`${API}/summaries/${recordingId}`, auth()),
      ]);
      if (tRes.status === 'fulfilled') setTranscript(tRes.value.data);
      if (aRes.status === 'fulfilled') setAnalysis(aRes.value.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load transcript');
    } finally {
      setLoading(false);
    }
  }, [recordingId]);

  useEffect(() => { loadData(); }, [loadData]);

  const copyTranscript = () => {
    if (!transcript) return;
    navigator.clipboard.writeText(transcript.transcript_text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadTranscript = () => {
    if (!transcript) return;
    const blob = new Blob([transcript.transcript_text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript-${recordingId}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const highlightText = (text: string, term: string) => {
    if (!term.trim()) return text;
    const parts = text.split(new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((p, i) =>
      p.toLowerCase() === term.toLowerCase()
        ? `<mark style="background:#fbbf24;color:#1f2937;border-radius:2px">${p}</mark>`
        : p
    ).join('');
  };

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  if (loading) return (
    <div style={containerStyle}>
      <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
        Loading transcript…
      </div>
    </div>
  );

  if (error) return (
    <div style={containerStyle}>
      <button onClick={onBack} style={backBtn}>← Back</button>
      <div style={{ textAlign: 'center', padding: 60, color: '#ef4444' }}>⚠ {error}</div>
    </div>
  );

  if (!transcript) return (
    <div style={containerStyle}>
      <button onClick={onBack} style={backBtn}>← Back</button>
      <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>📄</div>
        <div style={{ fontSize: 16, color: '#475569' }}>Transcript not yet available</div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>
          Processing may still be in progress.
        </div>
      </div>
    </div>
  );

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={backBtn}>← Back</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            📞 {transcript.original_name || transcript.filename}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            {transcript.username} · {transcript.word_count} words ·
            {transcript.duration_seconds && ` ${formatTime(Number(transcript.duration_seconds))} · `}
            Lang: {transcript.language?.toUpperCase()} · Model: {transcript.whisper_model}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={copyTranscript} style={btnSmall}>{copied ? '✓ Copied' : '📋 Copy'}</button>
          <button onClick={downloadTranscript} style={btnSmall}>⬇ Download</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #1e293b', marginBottom: 20, gap: 4, flexWrap: 'wrap' }}>
        {[
          ['transcript', '📝 Transcript'],
          ['summary', '📋 Summary'],
          ['sentiment', '😊 Sentiment'],
          ['actions', '✅ Action Items'],
        ].map(([t, label]) => (
          <button key={t} onClick={() => setTab(t as any)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '10px 16px', fontSize: 13, fontWeight: tab === t ? 700 : 400,
            color: tab === t ? '#c4b5fd' : '#64748b',
            borderBottom: tab === t ? '2px solid #8b5cf6' : '2px solid transparent',
          }}>
            {label}
          </button>
        ))}
      </div>

      {/* Transcript tab */}
      {tab === 'transcript' && (
        <div>
          <div style={{ marginBottom: 14 }}>
            <input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="🔍 Search in transcript…"
              style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 8, padding: '8px 14px', fontSize: 13, boxSizing: 'border-box' }}
            />
          </div>

          {transcript.segments && transcript.segments.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {transcript.segments
                .filter(s => !searchTerm || s.text.toLowerCase().includes(searchTerm.toLowerCase()))
                .map((seg, i) => (
                  <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 14px', background: '#1e293b', borderRadius: 8 }}>
                    <div style={{ fontSize: 11, color: '#6366f1', fontFamily: 'monospace', minWidth: 70, paddingTop: 2 }}>
                      {formatTime(seg.start)} → {formatTime(seg.end)}
                    </div>
                    <div
                      style={{ flex: 1, fontSize: 14, color: '#e2e8f0', lineHeight: 1.6 }}
                      dangerouslySetInnerHTML={{ __html: highlightText(seg.text, searchTerm) }}
                    />
                  </div>
                ))}
            </div>
          ) : (
            <div
              style={{ background: '#1e293b', borderRadius: 10, padding: '18px 20px', fontSize: 14, color: '#e2e8f0', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}
              dangerouslySetInnerHTML={{ __html: highlightText(transcript.transcript_text, searchTerm) }}
            />
          )}
        </div>
      )}

      {/* Summary tab */}
      {tab === 'summary' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {analysis ? (
            <>
              <div style={cardStyle}>
                <div style={cardTitle}>Executive Summary</div>
                <p style={{ color: '#e2e8f0', lineHeight: 1.7, margin: 0 }}>{analysis.executive_summary || '—'}</p>
              </div>
              <div style={cardStyle}>
                <div style={cardTitle}>Detailed Summary</div>
                <p style={{ color: '#cbd5e1', lineHeight: 1.7, margin: 0 }}>{analysis.detailed_summary || '—'}</p>
              </div>
              {analysis.topics && analysis.topics.length > 0 && (
                <div style={cardStyle}>
                  <div style={cardTitle}>Topics Discussed</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                    {analysis.topics.map((t, i) => (
                      <span key={i} style={{ background: '#6366f122', color: '#a5b4fc', borderRadius: 20, padding: '4px 12px', fontSize: 12 }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}
              {analysis.keywords && analysis.keywords.length > 0 && (
                <div style={cardStyle}>
                  <div style={cardTitle}>Keywords</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                    {analysis.keywords.map((k, i) => (
                      <span key={i} style={{ background: '#0f172a', color: '#94a3b8', border: '1px solid #334155', borderRadius: 4, padding: '3px 10px', fontSize: 11 }}>{k}</span>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>🤖</div>
              AI analysis not yet available. Enable summary feature in settings.
            </div>
          )}
        </div>
      )}

      {/* Sentiment tab */}
      {tab === 'sentiment' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {analysis?.sentiment ? (
            <>
              <div style={{ ...cardStyle, textAlign: 'center', padding: '32px 20px' }}>
                <div style={{ fontSize: 48, marginBottom: 8 }}>
                  {analysis.sentiment === 'positive' ? '😊' : analysis.sentiment === 'negative' ? '😞' : analysis.sentiment === 'mixed' ? '😐' : '😑'}
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color: SENTIMENT_COLOR[analysis.sentiment] || '#64748b', textTransform: 'capitalize' }}>
                  {analysis.sentiment}
                </div>
                <div style={{ fontSize: 14, color: '#64748b', marginTop: 6 }}>
                  Score: {analysis.sentiment_score > 0 ? '+' : ''}{Number(analysis.sentiment_score).toFixed(2)}
                </div>
                {/* Score bar */}
                <div style={{ margin: '16px auto', maxWidth: 300, height: 8, background: '#1e293b', borderRadius: 4, position: 'relative' }}>
                  <div style={{
                    position: 'absolute', height: '100%', borderRadius: 4,
                    background: SENTIMENT_COLOR[analysis.sentiment] || '#64748b',
                    width: `${Math.abs(Number(analysis.sentiment_score)) * 50}%`,
                    left: Number(analysis.sentiment_score) >= 0 ? '50%' : `${50 - Math.abs(Number(analysis.sentiment_score)) * 50}%`,
                  }} />
                  <div style={{ position: 'absolute', left: '50%', top: -2, width: 2, height: 12, background: '#475569' }} />
                </div>
              </div>

              {analysis.sentiment_detail && Object.keys(analysis.sentiment_detail).length > 0 && (
                <div style={cardStyle}>
                  <div style={cardTitle}>Sentiment Breakdown</div>
                  {analysis.sentiment_detail.customer && (
                    <div style={{ marginTop: 12, padding: '10px 14px', background: '#0f172a', borderRadius: 8 }}>
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>Customer: </span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: SENTIMENT_COLOR[analysis.sentiment_detail.customer] || '#94a3b8', textTransform: 'capitalize' }}>{analysis.sentiment_detail.customer}</span>
                    </div>
                  )}
                  {analysis.sentiment_detail.agent && (
                    <div style={{ marginTop: 8, padding: '10px 14px', background: '#0f172a', borderRadius: 8 }}>
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>Agent: </span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: SENTIMENT_COLOR[analysis.sentiment_detail.agent] || '#94a3b8', textTransform: 'capitalize' }}>{analysis.sentiment_detail.agent}</span>
                    </div>
                  )}
                  {analysis.sentiment_detail.overall && (
                    <div style={{ marginTop: 12, fontSize: 13, color: '#cbd5e1', lineHeight: 1.6 }}>{analysis.sentiment_detail.overall}</div>
                  )}
                  {analysis.sentiment_detail.key_moments && analysis.sentiment_detail.key_moments.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Key Moments</div>
                      {analysis.sentiment_detail.key_moments.map((m: string, i: number) => (
                        <div key={i} style={{ fontSize: 12, color: '#94a3b8', padding: '6px 0', borderBottom: '1px solid #1e293b' }}>• {m}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>📊</div>
              Sentiment analysis not yet available.
            </div>
          )}
        </div>
      )}

      {/* Action Items tab */}
      {tab === 'actions' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {analysis?.action_items && analysis.action_items.length > 0 ? (
            analysis.action_items.map((item, i) => (
              <div key={i} style={{ ...cardStyle, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', background: PRIORITY_COLOR[item.priority] + '22',
                  border: `2px solid ${PRIORITY_COLOR[item.priority] || '#64748b'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, color: PRIORITY_COLOR[item.priority] || '#64748b', fontWeight: 700, flexShrink: 0,
                }}>
                  {i + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, color: '#e2e8f0', lineHeight: 1.5 }}>{item.item}</div>
                  <div style={{ marginTop: 6, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>👤 {item.assignee}</span>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 4,
                      background: PRIORITY_COLOR[item.priority] + '22',
                      color: PRIORITY_COLOR[item.priority] || '#64748b', fontWeight: 700,
                      textTransform: 'capitalize',
                    }}>
                      {item.priority}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>✅</div>
              No action items found{!analysis ? ' — enable action items feature in settings' : ''}.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const containerStyle: React.CSSProperties = {
  flex: 1, overflowY: 'auto', padding: '24px 28px',
  background: '#0f172a', color: '#e2e8f0',
};
const backBtn: React.CSSProperties = {
  background: '#1e293b', border: '1px solid #334155', color: '#94a3b8',
  borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 600,
};
const btnSmall: React.CSSProperties = {
  background: '#1e293b', border: '1px solid #334155', color: '#94a3b8',
  borderRadius: 8, padding: '6px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600,
};
const cardStyle: React.CSSProperties = {
  background: '#1e293b', borderRadius: 12, padding: '18px 20px',
};
const cardTitle: React.CSSProperties = {
  fontSize: 12, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase',
  letterSpacing: 1, marginBottom: 10,
};
